const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') {
    return ''
  }

  const { hostname, protocol } = window.location

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4010`
  }

  return window.location.origin
}

const mainSiteUrl = stripTrailingSlash(viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl())
const authApiBaseUrl = stripTrailingSlash(
  viteEnv.VITE_AUTH_API_BASE_URL ?? resolveLocalMainSiteUrl(),
)

function resolveApiBaseUrl() {
  if (authApiBaseUrl) return authApiBaseUrl
  if (mainSiteUrl) return mainSiteUrl
  return ''
}

function resolveEndpoint(explicitValue: string | undefined, fallbackPath: string): string {
  if (explicitValue && explicitValue.trim()) {
    return explicitValue.trim()
  }

  const apiBaseUrl = resolveApiBaseUrl()
  if (apiBaseUrl) {
    return `${apiBaseUrl}${fallbackPath}`
  }

  return fallbackPath
}

const ALIYUN_TTS_ENDPOINT = resolveEndpoint(
  viteEnv.VITE_SUBSITE_ALIYUN_TTS_ENDPOINT,
  '/api/subsite/tts/aliyun',
)

export type VoiceGender = 'male' | 'female' | 'neutral'
export type VoiceAge = 'child' | 'teen' | 'young_adult' | 'middle_aged' | 'senior'
export type VoicePitch = 'high' | 'mid' | 'low' | 'slightly_high' | 'slightly_low'
export type VoiceSpeed = 'fast' | 'medium' | 'slow' | 'slightly_fast' | 'slightly_slow'
export type VoiceEmotion =
  | 'bright'
  | 'steady'
  | 'gentle'
  | 'serious'
  | 'lively'
  | 'calm'
  | 'healing'
export type VoiceTrait = 'magnetic' | 'crisp' | 'husky' | 'rounded' | 'sweet' | 'rich' | 'powerful'
export type VoiceUseCase =
  | 'news'
  | 'advertising'
  | 'audiobook'
  | 'animation'
  | 'assistant'
  | 'documentary'

export type AliyunVoiceTagOption<T extends string> = {
  value: T
  label: string
}

export type AliyunVoiceProfile = {
  value: string
  label: string
  description: string
  sourceModel: 'qwen-tts'
  gender: VoiceGender
  age: VoiceAge
  pitch: VoicePitch
  speed: VoiceSpeed
  emotions: VoiceEmotion[]
  traits: VoiceTrait[]
  useCases: VoiceUseCase[]
  languages: string[]
}

export const VOICE_GENDER_OPTIONS: readonly AliyunVoiceTagOption<VoiceGender>[] = [
  { value: 'female', label: '女性' },
  { value: 'male', label: '男性' },
  { value: 'neutral', label: '中性' },
] as const

export const VOICE_AGE_OPTIONS: readonly AliyunVoiceTagOption<VoiceAge>[] = [
  { value: 'child', label: '儿童（5-12 岁）' },
  { value: 'teen', label: '青少年（13-18 岁）' },
  { value: 'young_adult', label: '青年（19-35 岁）' },
  { value: 'middle_aged', label: '中年（36-55 岁）' },
  { value: 'senior', label: '老年（55 岁以上）' },
] as const

export const VOICE_PITCH_OPTIONS: readonly AliyunVoiceTagOption<VoicePitch>[] = [
  { value: 'high', label: '高音' },
  { value: 'mid', label: '中音' },
  { value: 'low', label: '低音' },
  { value: 'slightly_high', label: '偏高' },
  { value: 'slightly_low', label: '偏低' },
] as const

export const VOICE_SPEED_OPTIONS: readonly AliyunVoiceTagOption<VoiceSpeed>[] = [
  { value: 'fast', label: '快速' },
  { value: 'medium', label: '中速' },
  { value: 'slow', label: '缓慢' },
  { value: 'slightly_fast', label: '偏快' },
  { value: 'slightly_slow', label: '偏慢' },
] as const

export const VOICE_EMOTION_OPTIONS: readonly AliyunVoiceTagOption<VoiceEmotion>[] = [
  { value: 'bright', label: '开朗' },
  { value: 'steady', label: '沉稳' },
  { value: 'gentle', label: '温柔' },
  { value: 'serious', label: '严肃' },
  { value: 'lively', label: '活泼' },
  { value: 'calm', label: '冷静' },
  { value: 'healing', label: '治愈' },
] as const

export const VOICE_TRAIT_OPTIONS: readonly AliyunVoiceTagOption<VoiceTrait>[] = [
  { value: 'magnetic', label: '有磁性' },
  { value: 'crisp', label: '清脆' },
  { value: 'husky', label: '沙哑' },
  { value: 'rounded', label: '圆润' },
  { value: 'sweet', label: '甜美' },
  { value: 'rich', label: '浑厚' },
  { value: 'powerful', label: '有力' },
] as const

export const VOICE_USE_CASE_OPTIONS: readonly AliyunVoiceTagOption<VoiceUseCase>[] = [
  { value: 'news', label: '新闻播报' },
  { value: 'advertising', label: '广告配音' },
  { value: 'audiobook', label: '有声书' },
  { value: 'animation', label: '动画角色' },
  { value: 'assistant', label: '语音助手' },
  { value: 'documentary', label: '纪录片解说' },
] as const

export const ALIYUN_TTS_VOICE_PROFILES: readonly AliyunVoiceProfile[] = [
  {
    value: 'Cherry',
    label: '芊悦',
    description: '阳光、亲切、自然的中文女声，适合日常解说与产品介绍。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'slightly_high',
    speed: 'medium',
    emotions: ['bright', 'gentle', 'lively'],
    traits: ['sweet', 'crisp'],
    useCases: ['advertising', 'assistant', 'documentary'],
    languages: ['中文', '英文'],
  },
  {
    value: 'Serena',
    label: '苏瑶',
    description: '温柔、稳妥的女声，适合口播、课程和说明类内容。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'slightly_slow',
    emotions: ['gentle', 'steady', 'healing'],
    traits: ['rounded', 'sweet'],
    useCases: ['audiobook', 'assistant', 'documentary'],
    languages: ['中文', '英文'],
  },
  {
    value: 'Chelsie',
    label: '千雪',
    description: '更有角色感和表现力的女声，适合情绪化表达与角色演绎。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'high',
    speed: 'slightly_fast',
    emotions: ['lively', 'bright', 'gentle'],
    traits: ['sweet', 'crisp', 'powerful'],
    useCases: ['animation', 'advertising', 'assistant'],
    languages: ['中文', '英文', '日语'],
  },
  {
    value: 'Mia',
    label: '米娅',
    description: '轻快自然的年轻女声，适合短视频、品牌内容和轻量助手。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'slightly_high',
    speed: 'fast',
    emotions: ['bright', 'lively'],
    traits: ['crisp', 'sweet'],
    useCases: ['advertising', 'assistant', 'animation'],
    languages: ['中文', '英文'],
  },
  {
    value: 'Katerina',
    label: '凯特琳娜',
    description: '成熟大气的女声，适合国际化叙述、品牌旁白和纪录片。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'middle_aged',
    pitch: 'mid',
    speed: 'medium',
    emotions: ['steady', 'calm', 'serious'],
    traits: ['magnetic', 'rounded'],
    useCases: ['documentary', 'news', 'audiobook'],
    languages: ['英文', '法语'],
  },
  {
    value: 'Bella',
    label: '贝拉',
    description: '柔和甜美的女声，适合品牌讲述、儿童向内容和轻小说旁白。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'teen',
    pitch: 'high',
    speed: 'medium',
    emotions: ['gentle', 'bright', 'healing'],
    traits: ['sweet', 'rounded'],
    useCases: ['animation', 'audiobook', 'advertising'],
    languages: ['英文'],
  },
  {
    value: 'Jada',
    label: '洁达',
    description: '利落清晰的女声，适合节奏稍快的信息表达和广告内容。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'fast',
    emotions: ['bright', 'serious'],
    traits: ['crisp', 'powerful'],
    useCases: ['advertising', 'news', 'assistant'],
    languages: ['英文'],
  },
  {
    value: 'Sunny',
    label: '晴语',
    description: '明亮活泼的女声，适合营销视频、活动宣传和产品介绍。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'young_adult',
    pitch: 'slightly_high',
    speed: 'slightly_fast',
    emotions: ['bright', 'lively'],
    traits: ['crisp', 'sweet'],
    useCases: ['advertising', 'assistant', 'animation'],
    languages: ['中文', '英文'],
  },
  {
    value: 'Ethan',
    label: '艾森',
    description: '清晰、成熟、适合旁白的男声，整体稳健自然。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'medium',
    emotions: ['steady', 'calm'],
    traits: ['magnetic', 'rounded'],
    useCases: ['documentary', 'assistant', 'audiobook'],
    languages: ['中文', '英文'],
  },
  {
    value: 'Aiden',
    label: '艾登',
    description: '年轻明快的男声，适合节奏型广告、科技演示和互动播报。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'young_adult',
    pitch: 'slightly_high',
    speed: 'fast',
    emotions: ['bright', 'lively'],
    traits: ['crisp', 'powerful'],
    useCases: ['advertising', 'assistant', 'news'],
    languages: ['英文'],
  },
  {
    value: 'Eldric Sage',
    label: '圣哲',
    description: '低沉厚实、富有掌控感的男声，适合纪录片、品牌旁白和深度内容。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'middle_aged',
    pitch: 'low',
    speed: 'slow',
    emotions: ['steady', 'serious', 'calm'],
    traits: ['rich', 'magnetic', 'powerful'],
    useCases: ['documentary', 'news', 'audiobook'],
    languages: ['英文'],
  },
  {
    value: 'Jules',
    label: '朱尔斯',
    description: '偏中性的男声，适合技术讲解、教程和知识内容。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'young_adult',
    pitch: 'slightly_low',
    speed: 'medium',
    emotions: ['calm', 'steady'],
    traits: ['rounded', 'crisp'],
    useCases: ['assistant', 'documentary', 'news'],
    languages: ['英文'],
  },
  {
    value: 'Atlas',
    label: '阿特拉斯',
    description: '有力量感的中低音男声，适合品牌片、预告和重大信息传达。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'middle_aged',
    pitch: 'low',
    speed: 'medium',
    emotions: ['serious', 'steady'],
    traits: ['rich', 'powerful', 'magnetic'],
    useCases: ['advertising', 'documentary', 'news'],
    languages: ['英文'],
  },
  {
    value: 'Bree',
    label: '布里',
    description: '偏年轻、带清透感的中性声音，适合语音助手和产品导览。',
    sourceModel: 'qwen-tts',
    gender: 'neutral',
    age: 'teen',
    pitch: 'high',
    speed: 'medium',
    emotions: ['bright', 'calm'],
    traits: ['crisp', 'sweet'],
    useCases: ['assistant', 'animation', 'advertising'],
    languages: ['英文'],
  },
  {
    value: 'Cove',
    label: '科芙',
    description: '轻柔中性声线，适合陪伴类、冥想类和舒缓叙述内容。',
    sourceModel: 'qwen-tts',
    gender: 'neutral',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'slow',
    emotions: ['healing', 'gentle', 'calm'],
    traits: ['rounded', 'sweet'],
    useCases: ['assistant', 'audiobook', 'documentary'],
    languages: ['英文'],
  },
  {
    value: 'Rex',
    label: '雷克斯',
    description: '有舞台感的磁性男声，适合预告、广告和更强风格化表达。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'middle_aged',
    pitch: 'low',
    speed: 'slightly_fast',
    emotions: ['serious', 'bright'],
    traits: ['magnetic', 'husky', 'powerful'],
    useCases: ['advertising', 'animation', 'documentary'],
    languages: ['英文'],
  },
  {
    value: 'Marisol',
    label: '玛丽索尔',
    description: '有表现力的成熟女声，适合品牌讲述、国际广告和故事感内容。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'middle_aged',
    pitch: 'mid',
    speed: 'medium',
    emotions: ['steady', 'gentle'],
    traits: ['rounded', 'magnetic'],
    useCases: ['advertising', 'documentary', 'audiobook'],
    languages: ['西班牙语', '英文'],
  },
  {
    value: 'Suri',
    label: '苏莉',
    description: '清脆灵动的年轻女声，适合轻内容、角色台词和互动型播报。',
    sourceModel: 'qwen-tts',
    gender: 'female',
    age: 'teen',
    pitch: 'high',
    speed: 'slightly_fast',
    emotions: ['lively', 'bright'],
    traits: ['crisp', 'sweet'],
    useCases: ['animation', 'assistant', 'advertising'],
    languages: ['英文', '韩语'],
  },
  {
    value: 'Amir',
    label: '阿米尔',
    description: '稳健自然的男声，适合说明文、知识内容和多语叙述场景。',
    sourceModel: 'qwen-tts',
    gender: 'male',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'medium',
    emotions: ['steady', 'calm'],
    traits: ['rounded', 'crisp'],
    useCases: ['news', 'documentary', 'assistant'],
    languages: ['英文', '阿拉伯语'],
  },
  {
    value: 'Remy',
    label: '雷米',
    description: '中性偏冷静的声线，适合系统提示、产品导览和科技内容。',
    sourceModel: 'qwen-tts',
    gender: 'neutral',
    age: 'young_adult',
    pitch: 'mid',
    speed: 'medium',
    emotions: ['calm', 'serious'],
    traits: ['crisp', 'rounded'],
    useCases: ['assistant', 'news', 'documentary'],
    languages: ['英文', '法语'],
  },
] as const

export type AliyunTtsVoice = (typeof ALIYUN_TTS_VOICE_PROFILES)[number]['value']

export const ALIYUN_TTS_VOICE_OPTIONS = ALIYUN_TTS_VOICE_PROFILES.map((profile) => ({
  value: profile.value,
  label: profile.label,
  description: profile.description,
})) as ReadonlyArray<Pick<AliyunVoiceProfile, 'value' | 'label' | 'description'>>

export const ALIYUN_TTS_LANGUAGE_OPTIONS = [
  { value: 'Chinese', label: '中文' },
  { value: 'English', label: 'English' },
  { value: 'Japanese', label: '日本語' },
  { value: 'Korean', label: '한국어' },
  { value: 'French', label: 'Français' },
  { value: 'German', label: 'Deutsch' },
  { value: 'Spanish', label: 'Español' },
  { value: 'Portuguese', label: 'Português' },
  { value: 'Italian', label: 'Italiano' },
  { value: 'Russian', label: 'Русский' },
  { value: 'Auto', label: '自动识别' },
] as const

export type AliyunTtsLanguage = (typeof ALIYUN_TTS_LANGUAGE_OPTIONS)[number]['value']

export type AliyunVoiceFilterValue =
  | 'all'
  | VoiceGender
  | VoiceAge
  | VoicePitch
  | VoiceSpeed
  | VoiceEmotion
  | VoiceTrait
  | VoiceUseCase

export function getAliyunVoiceProfile(voice: string) {
  return ALIYUN_TTS_VOICE_PROFILES.find((profile) => profile.value === voice)
}

function createOutputFileName(text: string, voice: string) {
  const safeText =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'speech'
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-tts-${safeText}-${voice.toLowerCase().replace(/\s+/g, '-')}-aliyun-${timestamp}.wav`
}

class AliyunTtsService {
  isSupported(): boolean {
    return typeof fetch === 'function'
  }

  async generateSpeechFile({
    text,
    voice,
    languageType,
    instructions,
    optimizeInstructions,
    onProgress,
  }: {
    text: string
    voice: string
    languageType: AliyunTtsLanguage
    /** Optional natural-language speaking-style instructions (requires the
     * qwen3-tts-instruct-flash model on the backend). */
    instructions?: string
    /** When true, asks the backend to optimize the instructions prompt. */
    optimizeInstructions?: boolean
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; file: File; duration: number }> {
    const trimmedText = text.trim()
    if (!trimmedText) {
      throw new Error('请输入要合成的文本。')
    }

    onProgress?.('正在连接云端语音服务...')

    const body: Record<string, unknown> = {
      text: trimmedText,
      voice,
      languageType,
    }
    const trimmedInstructions = instructions?.trim()
    if (trimmedInstructions) {
      body.instructions = trimmedInstructions
      if (optimizeInstructions !== undefined) {
        body.optimizeInstructions = optimizeInstructions
      }
    }

    const response = await fetch(ALIYUN_TTS_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      let message = '云端语音请求失败。'

      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore invalid JSON body
      }

      throw new Error(message)
    }

    onProgress?.('正在接收云端音频...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('云端语音没有返回音频数据。')
    }

    // Normalize wav MIME variants (audio/x-wav, audio/wave) to the canonical
    // audio/wav so downstream upload validation and playback are consistent.
    const rawType = blob.type.toLowerCase()
    const mimeType =
      rawType === 'audio/x-wav' || rawType === 'audio/wave' ? 'audio/wav' : rawType || 'audio/wav'

    const file = new File([blob], createOutputFileName(trimmedText, voice), {
      type: mimeType,
      lastModified: Date.now(),
    })

    const duration = await this.readDuration(blob)
    return { blob, file, duration }
  }

  private async readDuration(blob: Blob): Promise<number> {
    if (typeof Audio === 'undefined') {
      return 0
    }

    const objectUrl = URL.createObjectURL(blob)

    try {
      const duration = await new Promise<number>((resolve) => {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.src = objectUrl
        audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0)
        audio.onerror = () => resolve(0)
      })
      return duration
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }
}

export const aliyunTtsService = new AliyunTtsService()
